      import React, { useMemo, useCallback } from 'react';
import { Loader, ArrowLeft, ArrowRight } from 'lucide-react';
import StatusBadge from './StatusBadge';

const ResponsiveTable = React.memo(({ data, onPageChange, isLoading, originalQuery, messageId }) => {
  const displayColumns = useMemo(() => {
    if (!data?.results?.length) return [];
    return Object.keys(data.results[0]).filter(col => 
      (!col.toLowerCase().endsWith('id') || ['invoiceNumber'].includes(col)) && 
      col.toLowerCase() !== 'applied' &&
      col !== 'recordId'
    );
  }, [data?.results]);
  
  const handlePrevPage = useCallback(() => {
    if (data.page > 1) {
      onPageChange(data.page - 1, messageId);
    }
  }, [data.page, onPageChange, messageId]);

  const handleNextPage = useCallback(() => {
    const maxPage = Math.ceil((data.total_count || data.results.length) / (data.page_size || 20));
    if (data.page < maxPage) {
      onPageChange(data.page + 1, messageId);
    }
  }, [data.page, data.total_count, data.results.length, data.page_size, onPageChange, messageId]);

  const getColumnConfig = useCallback((columnName) => {
    const name = columnName.toLowerCase();
    
    // Configuration for different column types
    if (name === 'invoicenumber') {
      return { width: '12%', minWidth: '120px', wrap: false };
    }
    if (name.includes('customer') || name.includes('name')) {
      return { width: '18%', minWidth: '150px', wrap: true };
    }
    if (name.includes('status') || name.includes('state')) {
      return { width: '18%', minWidth: '150px', wrap: false };
    }
    if (name.includes('date') || name.includes('time') || name === 'createdon') {
      return { width: '12%', minWidth: '120px', wrap: false };
    }
    if (name.includes('amount') || name.includes('paid') || name.includes('balance')) {
      return { width: '12%', minWidth: '110px', wrap: false };
    }
    // Default configuration
    return { width: '15%', minWidth: '140px', wrap: true };
  }, []);

  const isStatusColumn = (columnName) => {
    const name = columnName.toLowerCase();
    return name.includes('status') || name.includes('state');
  };

  if (!data?.results?.length) return null;

  const maxPage = Math.ceil((data.total_count || data.results.length) / (data.page_size || 20));
  const isPaginated = data.total_count > data.page_size;

  return (
    <div style={{ marginTop: '1.5rem', width: '1200px', maxWidth: '100%', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h4 style={{ margin: 0, color: '#1e293b' }}>
          Records ({data.total_count || data.results.length})
        </h4>
        {isLoading && isPaginated && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Loader style={{ width: '1rem', height: '1rem', animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Loading...</span>
          </div>
        )}
      </div>
      
      <div style={{ 
        backgroundColor: '#fff', 
        borderRadius: '8px', 
        border: '1px solid #e2e8f0', 
        overflow: 'hidden', 
        position: 'relative',
        width: '100%'
      }}>
        {isLoading && isPaginated && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
              <Loader style={{ width: '2rem', height: '2rem', animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>Loading...</span>
            </div>
          </div>
        )}
        
        <div style={{ overflowX: 'auto', padding: '1rem', width: '100%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#1e293b' }}>
                {displayColumns.map((col, index) => (
                  <th key={col} style={{ 
                    textAlign: 'left', 
                    padding: '0.75rem', 
                    color: 'white',
                    textTransform: 'uppercase', 
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    ...getColumnConfig(col),
                    position: 'relative'
                  }}>
                    {col.replace(/([A-Z])/g, ' $1').trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.results.map((row, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  {displayColumns.map((col) => {
                    const cellValue = row[col];
                    const isStatus = isStatusColumn(col);

                    const config = getColumnConfig(col);
                    const formattedValue = cellValue || '-';

                    return (
                      <td key={col} style={{ 
                        padding: '0.75rem', 
                        borderBottom: '1px solid #f1f5f9', 
                        fontSize: '0.85rem', 
                        color: '#374151',
                        whiteSpace: config.wrap ? 'normal' : 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        ...getColumnConfig(col)
                      }}>
                        {isStatus ? (
                          <StatusBadge status={cellValue} />
                        ) : (
                          <span title={formattedValue}>
                            {formattedValue}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {isPaginated && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
              Page {data.page} of {maxPage}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {data.page > 1 && (
                <button 
                  onClick={handlePrevPage}
                  disabled={isLoading}
                  style={{ 
                    padding: '0.5rem', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '4px', 
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    backgroundColor: '#fff',
                    opacity: isLoading ? 0.5 : 1
                  }}
                >
                  <ArrowLeft style={{ width: '1rem', height: '1rem' }} />
                </button>
              )}
              {data.page < maxPage && (
                <button 
                  onClick={handleNextPage}
                  disabled={isLoading}
                  style={{ 
                    padding: '0.5rem', 
                    backgroundColor: isLoading ? '#94a3b8' : '#4F7CFF', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.5 : 1
                  }}
                >
                  <ArrowRight style={{ width: '1rem', height: '1rem' }} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ResponsiveTable;